import {
  cloneTag,
  compareTags,
  isMeaningfulTag,
  normalizeLabel,
  normalizeNote,
  normalizeTarget,
  orphanTagIds,
  type SemanticTag,
  type TagTarget,
} from './SemanticTag';
import { fitTagImages, totalImageBytes, type TagImage } from './TagImage';

/**
 * The live set of semantic tags on a site.
 *
 * A plain keyed collection with an index by target, deliberately: every consumer asks one of two
 * questions — "what is tagged on this thing?" (the inspector, the manifest) or "what tags are there
 * at all?" (persistence, the tag list) — and both should be answerable without walking the array.
 *
 * Records are copied on the way in and out. The store is read by the capture pipeline while the
 * editor is still live, and a manifest that aliased editor state could describe a tag the user has
 * since retyped — which is the one failure mode a capture must not have.
 */

/** What changed, so a listener can decide whether it needs to rebuild anything expensive. */
export interface TagChange {
  kind: 'added' | 'updated' | 'removed' | 'loaded';
  tagIds: readonly string[];
}

export type TagListener = (change: TagChange) => void;

/** Key used by the target index. Two tags on the same thing share one. */
function targetKey(target: TagTarget): string {
  switch (target.kind) {
    case 'plant':
      return `plant:${target.instanceId}`;
    case 'structure':
      return `structure:${target.objectId}`;
    case 'ground':
      return `ground:${target.layer}`;
    case 'site':
      return 'site';
    case 'location':
      // Locations are not indexed by identity — there is no id to share — so every one is its own
      // bucket. Callers that want the tags near a point use `near`, which is a spatial query.
      return `location:${target.x},${target.z}`;
  }
}

export class TagStore {
  private readonly byId = new Map<string, SemanticTag>();
  private readonly listeners = new Set<TagListener>();

  get count(): number {
    return this.byId.size;
  }

  /** Every tag, loudest first. */
  all(): SemanticTag[] {
    return [...this.byId.values()].map(cloneTag).sort(compareTags);
  }

  get(id: string): SemanticTag | undefined {
    const tag = this.byId.get(id);
    return tag ? cloneTag(tag) : undefined;
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  /**
   * Add a tag, or replace one with the same id.
   *
   * Silently ignores an empty label rather than storing it: a tag with nothing to say is the state a
   * half-filled dialog is in, and persisting it would put blank lines into every future prompt.
   */
  add(tag: SemanticTag): void {
    const normalized = normalize(tag);
    if (!isMeaningfulTag(normalized)) return;
    const existed = this.byId.has(normalized.id);
    this.byId.set(normalized.id, normalized);
    this.emit({ kind: existed ? 'updated' : 'added', tagIds: [normalized.id] });
  }

  /** Patch a tag in place. Returns the tag as it now stands, or undefined when the id is unknown. */
  update(
    id: string,
    patch: Partial<Pick<SemanticTag, 'label' | 'note' | 'category' | 'emphasis' | 'target' | 'images'>>,
  ): SemanticTag | undefined {
    const current = this.byId.get(id);
    if (!current) return undefined;

    const next: SemanticTag = { ...current };
    if (patch.label !== undefined) next.label = normalizeLabel(patch.label);
    if (patch.category !== undefined) next.category = patch.category;
    if (patch.emphasis !== undefined) next.emphasis = patch.emphasis;
    if (patch.target !== undefined) next.target = normalizeTarget(patch.target);
    if (patch.note !== undefined) {
      const note = normalizeNote(patch.note);
      if (note === undefined) delete next.note;
      else next.note = note;
    }
    if (patch.images !== undefined) {
      // An empty list is how the UI says "I removed the last picture", so it clears the field
      // rather than being read as "no change" — the same rule the note field follows.
      const images = fitTagImages(patch.images);
      if (images.length === 0) delete next.images;
      else next.images = images;
    }

    if (!isMeaningfulTag(next)) {
      // Clearing the label is how the UI says "delete this", so honour it as one.
      this.remove(id);
      return undefined;
    }

    this.byId.set(id, next);
    this.emit({ kind: 'updated', tagIds: [id] });
    return cloneTag(next);
  }

  remove(id: string): SemanticTag | undefined {
    const tag = this.byId.get(id);
    if (!tag) return undefined;
    this.byId.delete(id);
    this.emit({ kind: 'removed', tagIds: [id] });
    return tag;
  }

  /** Replace the whole set — a project load. Fires one notification, not one per tag. */
  load(tags: readonly SemanticTag[]): void {
    this.byId.clear();
    for (const tag of tags) {
      const normalized = normalize(tag);
      if (isMeaningfulTag(normalized)) this.byId.set(normalized.id, normalized);
    }
    this.emit({ kind: 'loaded', tagIds: [...this.byId.keys()] });
  }

  clear(): void {
    if (this.byId.size === 0) return;
    const ids = [...this.byId.keys()];
    this.byId.clear();
    this.emit({ kind: 'loaded', tagIds: ids });
  }

  /* ---------------------------------- Queries ---------------------------------- */

  /** Tags attached to one target, loudest first. */
  forTarget(target: TagTarget): SemanticTag[] {
    const key = targetKey(target);
    return this.all().filter((tag) => targetKey(tag.target) === key);
  }

  forPlant(instanceId: string): SemanticTag[] {
    return this.forTarget({ kind: 'plant', instanceId });
  }

  forStructure(objectId: string): SemanticTag[] {
    return this.forTarget({ kind: 'structure', objectId });
  }

  /** Whole-site tags — the register the entire render should sit inside. */
  forSite(): SemanticTag[] {
    return this.forTarget({ kind: 'site' });
  }

  /** Every location tag, loudest first. */
  locations(): SemanticTag[] {
    return this.all().filter((tag) => tag.target.kind === 'location');
  }

  /** Every attached picture on the site, and what they cost the document together. */
  imageUsage(): { images: TagImage[]; bytes: number; tagCount: number } {
    const tagged = this.all().filter((tag) => (tag.images ?? []).length > 0);
    const images = tagged.flatMap((tag) => tag.images ?? []);
    return { images, bytes: totalImageBytes(images), tagCount: tagged.length };
  }

  /**
   * Location tags whose circle contains a point.
   *
   * Used to answer "what is this ground for?" when a plant or a click lands inside a tagged area —
   * so a shrub standing in the fire-pit lounge is described as being in it, rather than the tag
   * applying only to whatever was under the exact pixel the tag was dropped on.
   */
  near(x: number, z: number, extraMargin = 0): SemanticTag[] {
    return this.locations().filter((tag) => {
      if (tag.target.kind !== 'location') return false;
      const distance = Math.hypot(tag.target.x - x, tag.target.z - z);
      return distance <= tag.target.radiusMeters + Math.max(0, extraMargin);
    });
  }

  /**
   * Drop tags whose plant or structure has gone.
   *
   * Returns what was removed so the caller can put it on the undo record alongside the delete that
   * caused it — a tag that cannot come back with its bench is a tag the user has silently lost.
   */
  pruneOrphans(livePlantIds: ReadonlySet<string>, liveObjectIds: ReadonlySet<string>): SemanticTag[] {
    const ids = orphanTagIds([...this.byId.values()], livePlantIds, liveObjectIds);
    const removed: SemanticTag[] = [];
    for (const id of ids) {
      const tag = this.byId.get(id);
      if (!tag) continue;
      removed.push(cloneTag(tag));
      this.byId.delete(id);
    }
    if (removed.length > 0) this.emit({ kind: 'removed', tagIds: ids });
    return removed;
  }

  /* --------------------------------- Notification --------------------------------- */

  onChange(listener: TagListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(change: TagChange): void {
    for (const listener of [...this.listeners]) listener(change);
  }

  dispose(): void {
    this.listeners.clear();
    this.byId.clear();
  }
}

function normalize(tag: SemanticTag): SemanticTag {
  const copy = cloneTag(tag);
  copy.label = normalizeLabel(copy.label);
  copy.target = normalizeTarget(copy.target);
  const note = normalizeNote(copy.note);
  if (note === undefined) delete copy.note;
  else copy.note = note;
  return copy;
}
